<template>
  <picture>
    <!-- WebP format for modern browsers -->
    <source v-if="srcSetWebp" type="image/webp" :srcset="srcSetWebp" :sizes="sizes" />
    <!-- JPEG fallback for older browsers -->
    <source v-if="srcSetJpg" type="image/jpeg" :srcset="srcSetJpg" :sizes="sizes" />
    <!-- Fallback img element (used if <source> types aren't supported) -->
    <img
      :src="imgFallback"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      class="w-full object-cover"
      v-bind="$attrs"
    />
  </picture>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { buildUrl } from 'cloudinary-build-url'
import { STORAGE_TYPES } from '@cld-apis/utils'
import { useRuntimeConfig } from 'nuxt/app'

const config = useRuntimeConfig()

const props = defineProps({
  src: { type: String, required: true }, // full Contentful URL (starts with https:)
  alt: { type: String, default: '' },
  width: { type: [Number, String], default: 1200 },
  height: { type: [Number, String], default: 600 },
  // Responsive breakpoints for srcset
  breakpoints: { type: Array as () => number[], default: () => [480, 768, 1024, 1200] },
  sizes: { type: String, default: '(max-width: 768px) 100vw, 1200px' },
  loading: { type: String as PropType<'lazy' | 'eager'>, default: 'lazy' },

  cloudName: { type: String, default: '' },
  storageType: { type: String, default: STORAGE_TYPES.FETCH },
})

/**
 * Generates a srcset string for a given format (e.g., webp, jpg)
 * by applying Cloudinary transformations using the `fetch` method
 */
function generateSrcSet(format: string): string {
  return props.breakpoints
    .map(
      bp =>
        buildUrl(props.src, {
          cloud: {
            cloudName: props.cloudName || config.public.cloudname,
            storageType: props.storageType,
          },
          transformations: {
            format,
            resize: {
              type: 'fill',
              width: bp,
              height: Math.round((+props.height / +props.width) * bp),
            },
          },
        }) + ` ${bp}w`,
    )
    .join(', ')
}

const srcSetWebp = computed(() => generateSrcSet('webp'))
const srcSetJpg = computed(() => generateSrcSet('jpg'))

/**
 * Fallback image URL used in the <img> tag.
 * This is a single version (non-responsive) of the image in JPG format.
 */
const imgFallback = computed(() =>
  buildUrl(props.src, {
    cloud: {
      cloudName: props.cloudName || config.public.cloudname,
      storageType: props.storageType,
    },
    transformations: {
      format: 'jpg',
      resize: {
        type: 'fill',
        width: +props.width,
        height: +props.height,
      },
    },
  }),
)
</script>
